/**
 * QR Phone standalone brand (v1.0) — ink-and-paper surfaces with a single Live
 * Green accent, an Ink docked rail, a dark thin-outline system instead of
 * shadows, and Space Grotesk type. Canonical source: qr-phone-caller/docs/brand/.
 *
 * This is a token set for `createAdminTheme` — to tweak the QR Phone look, edit
 * the values below, not the factory.
 */

import { PaletteOptions } from '@mui/material/styles';
import { BrandTokens, createAdminTheme } from './createAdminTheme';

const palette: PaletteOptions = {
  mode: 'light',
  // QR Phone Live Green — used sparingly (calls, actions, accents); always "go".
  primary: { main: '#16C47F', light: '#4FD79E', dark: '#0E9E63', contrastText: '#16191C' },
  // Slate.
  secondary: { main: '#6B7378', light: '#9B9892', dark: '#4F5256', contrastText: '#FFFFFF' },
  // Ink-and-paper canvas with an Ink-solid rail.
  background: { default: '#F6F4ED', paper: '#FFFFFF', appbar: '#F6F4ED', menu: '#16191C' } as any,
  text: { primary: '#16191C', secondary: '#6B7378' },
  // Canonical status hues (brand guide v1.0).
  error: { main: '#EF4444', light: '#FCA5A5', dark: '#B91C1C', contrastText: '#FFFFFF' },
  warning: { main: '#D97706', light: '#FBBF24', dark: '#92400E', contrastText: '#FFFFFF' },
  info: { main: '#3B82F6', light: '#60A5FA', dark: '#1E40AF', contrastText: '#FFFFFF' },
  success: { main: '#10B981', light: '#34D399', dark: '#047857', contrastText: '#FFFFFF' },
  divider: '#E3E0D7',
  grey: {
    50: '#F6F4ED',
    100: '#EDEAE2',
    200: '#E3E0D7',
    300: '#C9C6BE',
    400: '#9B9892',
    500: '#6B7378',
    600: '#4F5256',
    700: '#34373B',
    800: '#232629',
    900: '#16191C',
  },
};

export const qrPhoneTokens: BrandTokens = {
  brand: 'qr-phone',
  fontFamily: '"Space Grotesk", "Roboto", "Helvetica", "Arial", sans-serif',
  palette,
  // Live Green fill with ink text (the brand guide's own button example). Green
  // is the only accent, so the CTA fill IS the primary color.
  ctaFill: '#16C47F',
  ctaFillHover: '#0E9E63',
  containedText: '#16191C',
  focusRing: '0 0 0 3px rgba(22, 196, 127, 0.28)',
  // Dark, thin outline over the paper canvas (Slate) — drives cards, datagrid
  // frame/header and neutral chips; card definition comes from the border, not
  // a shadow, so cardBorder === borderStrong and cardShadow is none.
  borderStrong: '#6B7378',
  borderRow: '#E3E0D7',
  cardBorder: '#6B7378',
  cardShadow: 'none',
  datagridFrameBorder: '#6B7378',
  dangerHover: '#991B1B',
  avatarBg: '#EDEAE2',
  avatarColor: '#6B7378',
  textMicro: { tertiary: '#5C6165', label: '#6B7378', idMono: '#9B9892' },
  tableHeaderBg: '#F1EEE7',
  headCellColor: '#16191C',
  shadows: {
    none: '0 1px 2px 0 rgb(0 0 0 / 0.01)',
    subtle: '0 1px 2px rgba(80,60,25,0.04), 0 4px 16px rgba(80,60,25,0.05)',
    medium: '0 4px 12px rgba(80,60,25,0.10), 0 12px 32px rgba(80,60,25,0.08)',
    strong: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    login: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
  },
};

export const qrPhoneTheme = createAdminTheme(qrPhoneTokens);

export default qrPhoneTheme;
